import styled from "@emotion/styled";
import classNames from "classnames";
import type { FunctionComponent } from "react";

import type { Props as BaseLinkProps } from "~/components/common/base-link";
import { BaseLink } from "~/components/common/base-link";
import type { ContentSchemaRenderProps } from "~/components/content/content-schema-render";
import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { data as appData, config, tracking } from "~/data";
import { isAbsoluteUrl } from "~/lib/utils";

import { DigitalArchiveLink } from "./digital-archive-link";
import { AnchorRelationship, AnchorTarget } from "./enums";
import { StyledBaseLink, Text } from "./styles";

export type Props = {
  className?: string[];
  href?: string;
} & ContentSchemaRenderProps;

const Component: FunctionComponent<Props> = ({
  children,
  className,
  href = "",
  parentSchemaNode,
  reference,
  schemaNode: schemaNode,
  ...attribs
}) => {
  // Link added in drupal by system config automatically
  const isDrupalAutoLink = className?.includes("d8-auto-link");
  const regex = new RegExp("(^https?://)((www.)?scmp.com|.*.product-web.dev-2.scmp.tech)");
  const isInternalHref = regex.test(href);
  const isShowDigitalArchiveLink =
    !isDrupalAutoLink &&
    parentSchemaNode?.children?.length === 1 &&
    parentSchemaNode?.children?.[0].type === "a" &&
    isInternalHref;

  // Generate link with custom parameters
  const queryParameters: BaseLinkProps["query"] = {
    module: tracking.module.Inline,
    pgtype: tracking.pageType.Article,
  };
  if (isShowDigitalArchiveLink) queryParameters.module = tracking.module.HardLink;
  else if (isDrupalAutoLink) queryParameters.module = tracking.module.InlineAuto;

  const isInternalUrl = [
    appData.application.scmp.domain,
    appData.application.goldthread.domain,
    config.general.siteUrl,
  ].some(internalUrl => href.includes(internalUrl));
  let anchorTarget: OrUndefined<AnchorTarget>;
  let anchorRelationship: OrUndefined<string>;
  if (!isAbsoluteUrl(href) || !isInternalUrl || className?.includes("js-tracking-referral")) {
    if (!isInternalUrl) anchorRelationship = AnchorRelationship.NoFollow;
    anchorTarget = AnchorTarget.Blank;
  }

  const handleGATracking = () => {
    sendGATracking({
      action: isShowDigitalArchiveLink ? "Hard link/Click" : "Inline link/Click",
      category: "Article Inline Link",
      label: href,
    });
  };

  const BaseLinkAttributes = {
    ...attribs,
    anchorProps: { rel: anchorRelationship, target: anchorTarget },
    onClick: handleGATracking,
    pathname: href,
    query: queryParameters,
  };

  return isShowDigitalArchiveLink ? (
    // Note: passed style instead of using emotion because need to keep className
    //       clean for the purpose of GTM events
    <BaseLink style={{ inlineSize: "100%" }} {...BaseLinkAttributes} className={"link-text"}>
      <DigitalArchiveLink
        {...{
          ...attribs,
          href,
          reference,
          schemaNode,
        }}
      />
    </BaseLink>
  ) : (
    <StyledBaseLink {...BaseLinkAttributes} className={classNames(className)}>
      <Text> {children}</Text>
    </StyledBaseLink>
  );
};

Component.displayName = "Link";
export const Link = styled(Component)``;
